import { useMemo, useState, type MouseEvent } from 'react';
import { Box, Flex, Text, Group, Tooltip } from '@mantine/core';
import { IconArrowRight, IconBulb, IconEye, IconExternalLink, IconFileDescription } from '@tabler/icons';
import { decodeHtmlEntities } from '../../../utils/decodeHtmlEntities';
import type { OnePager } from '../../../api/discoveryAgent';
import { useThemeImpact, useImpactAggregationProperties } from '../../../hooks/ideaPage/useIdeaQueries';
import { formatBigMonetaryValue } from '../../ideaNiv/Funcs';
import ImpactSkeleton from '../../Themes/ImpactSkeleton';

const SKIP_CARD_CLICK_ATTR = 'data-explore-evidence-skip-card-click';

function eventPathIncludesSkipCardClick(
  e: { target: EventTarget | null; nativeEvent: Event }
): boolean {
  const n = e.nativeEvent;
  if (typeof n.composedPath === 'function') {
    return n.composedPath().some(
      (node) => node instanceof Element && node.hasAttribute(SKIP_CARD_CLICK_ATTR)
    );
  }
  return (e.target as HTMLElement | null)?.closest(`[${SKIP_CARD_CLICK_ATTR}]`) != null;
}

interface ExploreEvidencesCardProps {
  title: string;
  /** When provided, live impact data is fetched via useThemeImpact */
  evidenceIds?: string[];
  /** Fallback static counts used when evidenceIds is not provided (legacy path) */
  arrInfo?: number;
  customersNumber?: number;
  evidenceNumber?: number;
  isExpanded?: boolean;
  onLearnMore?: () => void;
  onCreateIdea?: () => void;
  linkedIdeaId?: string | null;
  onCollapseLeftPanel?: () => void;
  /** Existing one-pager for this artifact — if set, show "Show One-Pager" instead of "Create One-Pager" */
  existingOnePager?: OnePager | null;
  isCreatingOnePager?: boolean;
  onCreateOnePager?: () => void;
  onViewOnePager?: () => void;
}

function MetricBlock({ value, label }: { value: string; label: string }) {
  return (
    <Box>
      <Text size="lg" fw={700} color="dark.9" lh={1.2}>
        {value}
      </Text>
      <Text size="xs" c="dimmed" mt={2}>
        {label}
      </Text>
    </Box>
  );
}

function getImpactPropertyValue(themeImpact: Record<string, unknown> | null, property: any): number {
  if (!themeImpact || !property) return 0;
  const impactData = themeImpact[property.propertyName] as any;
  if (!impactData) return 0;
  if (property.groupBy && property.impact?.groupByPath) {
    const groupKey = property.impact.groupByPath;
    return impactData[groupKey]?.count || impactData[groupKey]?.total || 0;
  }
  if (typeof impactData === 'object' && impactData.total !== undefined) {
    return impactData.total || 0;
  }
  return impactData || 0;
}

function formatImpactPropertyValue(property: any, value: number): string {
  const isCompanies = ['orgId', 'company._id'].includes(property?.fieldPath);
  return isCompanies
    ? String(value)
    : (formatBigMonetaryValue(value, property?.impact?.prefix ?? '$') ?? '—');
}

export const ExploreEvidencesCard = ({
  title,
  evidenceIds,
  arrInfo,
  customersNumber,
  evidenceNumber,
  isExpanded = false,
  onLearnMore,
  onCreateIdea,
  linkedIdeaId,
  onCollapseLeftPanel,
  existingOnePager,
  isCreatingOnePager = false,
  onCreateOnePager,
  onViewOnePager,
}: ExploreEvidencesCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasLiveImpact = !!evidenceIds && evidenceIds.length > 0;

  const { data: impactData, isLoading: impactLoading } = useThemeImpact(
    hasLiveImpact ? evidenceIds : undefined
  );
  const { data: impactAggPropsData, isLoading: impactPropsLoading } = useImpactAggregationProperties();

  const themeImpact = useMemo(() => {
    if (impactData?.aggregations?.[0]) return impactData.aggregations[0] as Record<string, unknown>;
    return null;
  }, [impactData]);

  const topImpactProperties = useMemo(() => {
    const rawProps =
      (impactAggPropsData as any)?.impactAggregationProperties ??
      (impactAggPropsData as any) ??
      [];
    if (!Array.isArray(rawProps) || rawProps.length === 0) return [];
    return [...rawProps]
      .sort((a: any, b: any) => (a.impact?.index ?? 0) - (b.impact?.index ?? 0))
      .slice(0, 2);
  }, [impactAggPropsData]);

  const handleClick = () => {
    onCollapseLeftPanel?.();
    onLearnMore?.();
  };

  const displayTitle = decodeHtmlEntities(title);

  const renderMetrics = () => {
    if (hasLiveImpact) {
      if (impactLoading || impactPropsLoading) {
        return <ImpactSkeleton count={topImpactProperties.length || 2} />;
      }
      return (
        <Flex gap="xl" align="flex-end">
          {topImpactProperties.map((property: any) => (
            <MetricBlock
              key={property.propertyName}
              value={formatImpactPropertyValue(
                property,
                getImpactPropertyValue(themeImpact, property)
              )}
              label={property.label || property.propertyName}
            />
          ))}
          <MetricBlock
            value={evidenceNumber !== undefined ? String(evidenceNumber) : '—'}
            label="Evidence"
          />
        </Flex>
      );
    }

    return (
      <Flex gap="xl" align="flex-end">
        {arrInfo !== undefined && (
          <MetricBlock value={formatBigMonetaryValue(arrInfo, '$') ?? '—'} label="Weighted ARR" />
        )}
        {customersNumber !== undefined && (
          <MetricBlock value={String(customersNumber)} label="Customers" />
        )}
        <MetricBlock
          value={evidenceNumber !== undefined ? String(evidenceNumber) : '—'}
          label="Evidence"
        />
      </Flex>
    );
  };

  const handleOpenIdeaLink = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(`/idea/${linkedIdeaId}`, '_blank', 'noopener,noreferrer');
  };

  const showOnePagerButton =
    Boolean((onCreateOnePager || existingOnePager) && hasLiveImpact && onLearnMore && !linkedIdeaId);
  const showCreateIdea =
    Boolean(onCreateIdea && hasLiveImpact && onLearnMore && !linkedIdeaId && !showOnePagerButton);

  return (
    <Box
      p="md"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        if (eventPathIncludesSkipCardClick(e)) {
          return;
        }
        e.stopPropagation();
        e.preventDefault();
        handleClick();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (eventPathIncludesSkipCardClick(e)) {
            return;
          }
          e.preventDefault();
          handleClick();
        }
      }}
      sx={{
        borderRadius: 8,
        border: isExpanded ? '1px solid #FF8700' : '1px solid #D8D8DB',
        backgroundColor: '#FAFAFA',
        boxShadow: '0px 1px 5px 0px #00124712',
        cursor: onLearnMore ? 'pointer' : 'default',
        transition: 'border-color 0.2s ease',
        '&:hover': onLearnMore ? { borderColor: isExpanded ? '#FF8700' : '#b0b0b5' } : undefined,
      }}
    >
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Flex direction="column" justify="flex-start" align="stretch" sx={{ flex: 1, minWidth: 0 }}>
          <Text fs={16} fw={600} color="dark.8" mb="sm" sx={{ minWidth: 0 }} lineClamp={2}>
            {displayTitle}
          </Text>
          {renderMetrics()}
        </Flex>
        <Flex
          direction="row"
          align="center"
          justify="flex-end"
          gap="sm"
          wrap="nowrap"
          sx={{ flexShrink: 0, alignSelf: 'flex-start' }}
        >
          {/* One-Pager button — Create or Show depending on whether one already exists */}
          {showOnePagerButton ? (
            <Box
              style={{ flexShrink: 0 }}
              sx={{ opacity: 1, pointerEvents: 'auto', transition: 'opacity 0.12s ease' }}
              aria-hidden={false}
              {...{ [SKIP_CARD_CLICK_ATTR]: '' }}
            >
              <Tooltip
                label={existingOnePager ? 'View the saved one-pager for this topic' : 'Generate a one-pager from this topic'}
                position="top"
                withArrow
                openDelay={400}
              >
                <Flex
                  align="center"
                  gap={5}
                  px={10}
                  py={5}
                  {...{ [SKIP_CARD_CLICK_ATTR]: '' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    e.nativeEvent.stopImmediatePropagation();
                    if (existingOnePager) {
                      onViewOnePager?.();
                    } else if (!isCreatingOnePager) {
                      onCreateOnePager?.();
                    }
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  role="button"
                  tabIndex={isHovered ? 0 : -1}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      if (existingOnePager) {
                        onViewOnePager?.();
                      } else if (!isCreatingOnePager) {
                        onCreateOnePager?.();
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 20,
                    border: '1px solid #5C5CEB55',
                    backgroundColor: '#5C5CEB0D',
                    cursor: isCreatingOnePager ? 'wait' : 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: '#5C5CEB1A',
                      borderColor: '#5C5CEB99',
                    },
                  }}
                >
                  {existingOnePager
                    ? <IconFileDescription size={13} color="#5C5CEB" style={{ flexShrink: 0 }} />
                    : <IconBulb size={13} color="#5C5CEB" style={{ flexShrink: 0 }} />
                  }
                  <Text size="xs" fw={500} color="#5C5CEB" sx={{ whiteSpace: 'nowrap' }}>
                    {isCreatingOnePager ? 'Creating…' : existingOnePager ? 'Show One-Pager' : 'Create One-Pager'}
                  </Text>
                </Flex>
              </Tooltip>
            </Box>
          ) : showCreateIdea ? (
            <Box
              style={{ flexShrink: 0 }}
              sx={{ opacity: 1, pointerEvents: 'auto', transition: 'opacity 0.12s ease' }}
              aria-hidden={false}
              {...{ [SKIP_CARD_CLICK_ATTR]: '' }}
            >
              <Tooltip label="Create an Idea from this topic" position="top" withArrow openDelay={400}>
                <Flex
                  align="center"
                  gap={5}
                  px={10}
                  py={5}
                  {...{ [SKIP_CARD_CLICK_ATTR]: '' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    e.nativeEvent.stopImmediatePropagation();
                    onCreateIdea?.();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  role="button"
                  tabIndex={isHovered ? 0 : -1}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onCreateIdea?.();
                    }
                  }}
                  sx={{
                    borderRadius: 20,
                    border: '1px solid #5C5CEB55',
                    backgroundColor: '#5C5CEB0D',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: '#5C5CEB1A',
                      borderColor: '#5C5CEB99',
                    },
                  }}
                >
                  <IconBulb size={13} color="#5C5CEB" style={{ flexShrink: 0 }} />
                  <Text size="xs" fw={500} color="#5C5CEB" sx={{ whiteSpace: 'nowrap' }}>
                    Create One-Pager
                  </Text>
                </Flex>
              </Tooltip>
            </Box>
          ) : null}
          {linkedIdeaId ? (
            <Box
              style={{ flexShrink: 0 }}
              sx={{
                opacity: 1,
                pointerEvents: 'auto',
                transition: 'opacity 0.12s ease',
              }}
              aria-hidden={false}
              {...{ [SKIP_CARD_CLICK_ATTR]: '' }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Group
                spacing={4}
                noWrap
                role="link"
                tabIndex={0}
                aria-label="Open linked idea in a new tab"
                {...{ [SKIP_CARD_CLICK_ATTR]: '' }}
                onClick={handleOpenIdeaLink}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (linkedIdeaId) {
                      window.open(`/idea/${linkedIdeaId}`, '_blank', 'noopener,noreferrer');
                    }
                  }
                }}
                sx={{
                  flexShrink: 0,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <Text size="sm" weight={500} c="#5C5CEB" style={{ fontFamily: 'Inter', fontSize: 14 }}>
                  Open linked idea
                </Text>
                <IconExternalLink size={14} stroke={1.5} color="#5C5CEB" />
              </Group>
            </Box>
          ) : null}
          {onLearnMore &&
            (isExpanded ? (
              <Flex align="center" gap={3} sx={{ color: '#585C68', flexShrink: 0 }}>
                <IconEye size={16} stroke={1.5} />
                <Text size="xs" inherit>
                  Expanded
                </Text>
              </Flex>
            ) : (
              <Text
                component="span"
                size="sm"
                c="#4B4BE8"
                fw={500}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, lineHeight: 1 }}
              >
                Learn more
                <IconArrowRight size={14} stroke={2} />
              </Text>
            ))}
        </Flex>
      </Flex>
    </Box>
  );
};

export default ExploreEvidencesCard;
